import './i18n/autoTranslate';
// ResetPassword.futuristic.jsx
import React, { useMemo, useState } from "react";
import {
  Eye,
  EyeOff,
  CheckCircle,
  ArrowLeft,
  Key,
  Loader2,
  Sparkles,
  ShieldCheck,
  Lock,
} from "lucide-react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";

import AppBackground from "../components/ui/AppBackground.tsx";
import SpotlightCard from "../components/ui/SpotlightCard";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";
import { useToast } from "../hooks/use-toast";
import { API_BASE_URL } from "../config/api";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const { toast } = useToast();

  const [formData, setFormData] = useState({ password: "", confirmPassword: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);

  const passwordRules = useMemo(
    () => [
      { label: "At least 6 characters", ok: (formData.password || "").length >= 6 },
      { label: "Passwords match", ok: !!formData.password && formData.password === formData.confirmPassword },
    ],
    [formData.password, formData.confirmPassword]
  );

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast({
        variant: "destructive",
        title: "Passwords don't match",
        description: "Please make sure both passwords are identical",
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        variant: "destructive",
        title: "Password too short",
        description: "Password must be at least 6 characters long",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: formData.password }),
      });

      if (response.ok) {
        setPasswordReset(true);
        toast({
          title: "Password Reset Successfully",
          description: "Your password has been updated successfully",
        });
        setTimeout(() => navigate("/login"), 2500);
      } else {
        const error = await response.json().catch(() => ({}));
        toast({
          variant: "destructive",
          title: "Reset Failed",
          description: error?.message || "Failed to reset password",
        });
      }
    } catch (error) {
      console.error("Error:", error);
      toast({
        variant: "destructive",
        title: "Connection Error",
        description: "Unable to connect to server. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const TopNav = () => (
    <nav
      className="fixed top-0 w-full py-4 px-4 sm:px-6 border-b border-white/10 bg-black/20 backdrop-blur-2xl"
      style={{ zIndex: 99999 }}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-purple-600/30 via-indigo-500/20 to-cyan-400/10 blur-xl" />
            <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10">
              <i className="ri-speak-ai-line text-3xl bg-gradient-to-r from-purple-400 to-indigo-300 bg-clip-text text-transparent" />
            </div>
          </div>

          <div className="flex flex-col leading-tight">
            <h1 className="text-sm sm:text-base font-semibold text-foreground">InterviewGenieAI</h1>
            <span className="text-[11px] text-muted-foreground">Secure Recovery Portal</span>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-sm text-white/70 hover:text-white transition-colors">
            Features
          </a>
          <Link to="/pricing" className="text-sm text-white/70 hover:text-white transition-colors">
            Pricing
          </Link>
          <a href="#faq" className="text-sm text-white/70 hover:text-white transition-colors">
            Resources
          </a>
        </div>

        <div className="flex items-center gap-3">
          <Link to="/sign-up" className="hidden sm:block text-sm text-white/70 hover:text-white transition-colors">
            Sign up
          </Link>
          <Link
            to="/login"
            className="relative inline-flex items-center justify-center px-4 py-2 rounded-xl text-sm font-semibold text-white
                       bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500
                       shadow-[0_12px_30px_-14px_rgba(99,102,241,0.8)] ring-1 ring-white/10 transition-all"
          >
            <span className="absolute inset-0 rounded-xl opacity-0 hover:opacity-100 transition-opacity bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.25),transparent_55%)]" />
            <span className="relative">Login</span>
          </Link>
        </div>
      </div>
    </nav>
  );

  const Shell = ({ children }) => (
    <AppBackground className="min-h-screen text-white overflow-hidden bg-[#07090c]">
      {/* Futuristic overlays */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_20%_10%,rgba(99,102,241,0.35),transparent_45%),radial-gradient(circle_at_80%_0%,rgba(168,85,247,0.30),transparent_45%),radial-gradient(circle_at_50%_100%,rgba(34,211,238,0.18),transparent_50%)]" />
        <div className="absolute inset-0 opacity-[0.18] grid-sweep [background-image:linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:64px_64px]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
        <div className="absolute -top-28 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-indigo-500/10 blur-[90px]" />
        <div className="absolute -bottom-40 right-[-120px] h-[520px] w-[520px] rounded-full bg-purple-500/10 blur-[100px]" />
      </div>

      <TopNav />

      <main className="relative z-10 pt-28 pb-24">{children}</main>

      <style>{`
        .grid-sweep { animation: gridSweep 18s linear infinite; will-change: transform; }
        @keyframes gridSweep {
          0% { transform: translate3d(0,0,0); }
          100% { transform: translate3d(-64px,-64px,0); }
        }
      `}</style>
    </AppBackground>
  );

  const LeftHero = ({ badge, title, subtitle, bullets }) => (
    <section className="space-y-6">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 backdrop-blur-xl">
        <Sparkles className="h-4 w-4 text-purple-300" />
        <span className="text-xs text-white/80">{badge}</span>
      </div>

      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-headline font-bold leading-tight">
        <span className="bg-gradient-to-r from-white via-white to-white/70 bg-clip-text text-transparent">{title}</span>{" "}
        <span className="bg-gradient-to-r from-purple-300 via-indigo-300 to-cyan-200 bg-clip-text text-transparent">
          {subtitle}
        </span>
      </h1>

      <p className="text-white/70 text-base sm:text-lg max-w-xl">
        Create a strong new password to secure your account. This reset link is time-limited and designed to keep your
        access protected.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
        {bullets?.map((b, idx) => (
          <div key={idx} className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl">
            <div className="flex items-center gap-2">
              {b.icon}
              <p className="font-semibold text-white">{b.title}</p>
            </div>
            <p className="mt-2 text-sm text-white/65">{b.desc}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 text-xs text-white/50">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400/80" />
        <span>Tip: use a unique password you don’t reuse elsewhere.</span>
      </div>
    </section>
  );

  const Card = ({ spotlight, children }) => (
    <SpotlightCard className="custom-spotlight-card rounded-3xl" spotlightColor={spotlight}>
      <div className="rounded-3xl p-[1px] bg-gradient-to-br from-white/15 via-white/5 to-transparent">
        <div className="rounded-3xl bg-[linear-gradient(180deg,rgba(15,18,30,0.72),rgba(10,12,20,0.62))] backdrop-blur-xl border border-white/10">
          {children}
        </div>
      </div>
    </SpotlightCard>
  );

  // 1) Missing token (invalid link)
  if (!token) {
    return (
      <Shell>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <LeftHero
              badge="Reset link required"
              title="This link"
              subtitle="is invalid."
              bullets={[
                {
                  icon: <Key className="h-5 w-5 text-red-300" />,
                  title: "Expired or malformed",
                  desc: "Reset links are temporary. Generate a fresh one to continue.",
                },
                {
                  icon: <ShieldCheck className="h-5 w-5 text-indigo-300" />,
                  title: "Security-first",
                  desc: "We don’t reveal account existence and protect your access by default.",
                },
              ]}
            />

            <section>
              <Card spotlight="rgba(239, 68, 68, 0.16)">
                <div className="px-6 sm:px-8 pt-7 pb-5 border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-red-500/25 via-purple-500/10 to-transparent blur-xl" />
                      <div className="relative w-11 h-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                        <Key className="h-6 w-6 text-red-300" />
                      </div>
                    </div>
                    <div className="leading-tight">
                      <h2 className="text-lg font-semibold text-white">Invalid reset link</h2>
                      <p className="text-sm text-white/60">Request a new link to reset your password.</p>
                    </div>
                  </div>
                </div>

                <div className="px-6 sm:px-8 py-7 space-y-5">
                  <div className="rounded-2xl border border-red-500/20 bg-red-500/10 p-4">
                    <p className="text-sm text-red-100/90">
                      This password reset link is invalid or has expired. Please request a new one.
                    </p>
                  </div>

                  <Link to="/forgot-password" className="block">
                    <Button
                      fullWidth
                      className="h-12 text-base font-semibold button-glow bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500"
                    >
                      Request New Reset Link
                    </Button>
                  </Link>

                  <Link to="/login" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
                    <ArrowLeft className="h-4 w-4" />
                    Back to Login
                  </Link>
                </div>
              </Card>
            </section>
          </div>
        </div>
      </Shell>
    );
  }

  // 2) Success state
  if (passwordReset) {
    return (
      <Shell>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <LeftHero
              badge="Access restored"
              title="Password"
              subtitle="updated."
              bullets={[
                {
                  icon: <CheckCircle className="h-5 w-5 text-emerald-300" />,
                  title: "Reset complete",
                  desc: "Your account is secured with the new password.",
                },
                {
                  icon: <ShieldCheck className="h-5 w-5 text-indigo-300" />,
                  title: "Safe redirect",
                  desc: "You’ll be taken to login automatically in a moment.",
                },
              ]}
            />

            <section>
              <Card spotlight="rgba(34, 197, 94, 0.18)">
                <div className="px-6 sm:px-8 pt-7 pb-5 border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-emerald-500/25 via-indigo-500/10 to-transparent blur-xl" />
                      <div className="relative w-11 h-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                        <CheckCircle className="h-6 w-6 text-emerald-300" />
                      </div>
                    </div>
                    <div className="leading-tight">
                      <h2 className="text-lg font-semibold text-white">Password reset successful</h2>
                      <p className="text-sm text-white/60">Redirecting you to login…</p>
                    </div>
                  </div>
                </div>

                <div className="px-6 sm:px-8 py-7 space-y-5">
                  <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4">
                    <p className="text-sm text-emerald-100/90">
                      You can now sign in with your new password.
                    </p>
                  </div>

                  <Link to="/login" className="block">
                    <Button
                      fullWidth
                      className="h-12 text-base font-semibold button-glow bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500"
                    >
                      Go to Login Now
                    </Button>
                  </Link>

                  <p className="text-xs text-white/45">
                    If you didn’t initiate this reset, contact support immediately.
                  </p>
                </div>
              </Card>
            </section>
          </div>
        </div>
      </Shell>
    );
  }

  // 3) Default form state
  return (
    <Shell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <LeftHero
            badge="Create a new password"
            title="Secure"
            subtitle="your account."
            bullets={[
              {
                icon: <ShieldCheck className="h-5 w-5 text-emerald-300" />,
                title: "Protected flow",
                desc: "Your link is signed and time-limited for extra security.",
              },
              {
                icon: <Lock className="h-5 w-5 text-indigo-300" />,
                title: "Stronger access",
                desc: "Pick a password that’s unique and hard to guess.",
              },
            ]}
          />

          <section>
            <Card spotlight="rgba(99, 102, 241, 0.18)">
              {/* Header */}
              <div className="px-6 sm:px-8 pt-7 pb-5 border-b border-white/10">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-purple-600/25 via-indigo-500/15 to-cyan-400/10 blur-xl" />
                      <div className="relative w-11 h-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                        <Lock className="h-6 w-6 text-indigo-200" />
                      </div>
                    </div>

                    <div className="leading-tight">
                      <h2 className="text-lg font-semibold text-white">Set a new password</h2>
                      <p className="text-sm text-white/60">Confirm it to finish the reset.</p>
                    </div>
                  </div>

                  <div className="hidden sm:flex items-center gap-2 text-xs text-white/45">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400/80" />
                    Link active
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="px-6 sm:px-8 py-7">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block font-medium mb-3 text-white/90">New Password</label>
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => handleChange("password", e.target.value)}
                      placeholder="Enter your new password"
                      required
                      icon={showPassword ? EyeOff : Eye}
                      onIconClick={() => setShowPassword((v) => !v)}
                      className="bg-white/5 border-white/10 text-white placeholder-white/35 h-12 text-base
                                 focus:border-indigo-400/40 focus:ring-2 focus:ring-indigo-500/20"
                    />
                  </div>

                  <div>
                    <label className="block font-medium mb-3 text-white/90">Confirm Password</label>
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) => handleChange("confirmPassword", e.target.value)}
                      placeholder="Confirm your new password"
                      required
                      icon={showConfirmPassword ? EyeOff : Eye}
                      onIconClick={() => setShowConfirmPassword((v) => !v)}
                      className="bg-white/5 border-white/10 text-white placeholder-white/35 h-12 text-base
                                 focus:border-indigo-400/40 focus:ring-2 focus:ring-indigo-500/20"
                    />
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl">
                    <p className="text-sm font-semibold text-white/90 mb-2">Password requirements</p>
                    <div className="space-y-2">
                      {passwordRules.map((r) => (
                        <div key={r.label} className="flex items-center gap-2 text-sm">
                          <span
                            className={`h-2 w-2 rounded-full ${
                              r.ok ? "bg-emerald-400/90" : "bg-white/20"
                            }`}
                          />
                          <span className={r.ok ? "text-white/80" : "text-white/55"}>{r.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    loading={loading}
                    fullWidth
                    className="h-12 text-base font-semibold button-glow
                               bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="animate-spin" />
                        Resetting…
                      </span>
                    ) : (
                      "Reset Password"
                    )}
                  </Button>

                  <div className="text-center pt-2">
                    <Link to="/login">
                      <Button
                        variant="ghost"
                        iconName={ArrowLeft}
                        iconPosition="left"
                        className="text-white/60 hover:text-white"
                      >
                        Back to Login
                      </Button>
                    </Link>
                  </div>
                </form>
              </div>
            </Card>
          </section>
        </div>
      </div>
    </Shell>
  );
}
